import { createApi } from "@reduxjs/toolkit/query/react";
import { VITE_API_V2_URL } from "codes";
import { axiosBaseQuery } from "helpers/axiosbaseQuery";

// Debug: console log base URL
const BASE_URL = `${VITE_API_V2_URL}`;
const ACCOUNTS_V2 = "/accounts";

export interface Agent {
  id: string;
  name: string;
}

export interface MapRebate {
  id: string;
  account_relationship_id: string;
  rebate_percentage: number;
  utilization_threshold_percentage: number;
  cycle_days: number;
  credit_limit_amount: number;
  first_invoice_funded_date: string | null;
  current_cycle_number: number;
  current_cycle_start_date: string | null;
  current_cycle_end_date: string | null;
  current_cycle_used_credit_amount: number;
  current_cycle_utilization_percentage: number;
  current_cycle_is_eligible: boolean;
  last_processed_cycle_number: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export type MapRebateUpdate = Partial<
  Pick<
    MapRebate,
    | "rebate_percentage"
    | "cycle_days"
    | "first_invoice_funded_date"
    | "current_cycle_number"
    | "current_cycle_start_date"
    | "current_cycle_end_date"
    | "current_cycle_is_eligible"
  >
>;

export const postcc = createApi({
  reducerPath: "postcc",
  tagTypes: [
    "ORIGINATION",
    "OPERATION",
    "LEGALE",
    "RISK",
    "DOCUSIGN_TEMPLATE",
    "REBATE",
  ],
  baseQuery: axiosBaseQuery({
    baseUrl: BASE_URL,
  }),
  endpoints: (builder) => ({
    GetMapRebate: builder.query<MapRebate, string>({
      query: (relationshipId) => ({
        url: `/maps/relationships/${encodeURIComponent(relationshipId)}/rebates`,
        method: "get",
      }),
      transformResponse: (response: { data: MapRebate }) => response.data,
      providesTags: (_result, _error, id) => [{ type: "REBATE", id }],
    }),
    UpdateMapRebate: builder.mutation<
      MapRebate,
      { relationshipId: string; data: MapRebateUpdate }
    >({
      query: ({ relationshipId, data }) => ({
        url: `/maps/relationships/${encodeURIComponent(relationshipId)}/rebates`,
        method: "patch",
        data,
      }),
      transformResponse: (response: { data: MapRebate }) => response.data,
      invalidatesTags: (_result, error, { relationshipId }) =>
        error ? [] : [{ type: "REBATE", id: relationshipId }],
    }),
    GetAllAgents: builder.query<Agent[], void>({
      query: () => ({ url: "/all-agent", method: "get" }),
      transformResponse: (response: { data: Agent[] }) => response.data,
    }),
    // Get fee details for origination
    GetFeeDetails: builder.query<any, { map_id: string }>({
      query: ({ map_id }) => ({
        url: `/origination/post-cc/maps/${map_id}`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
      providesTags: ["ORIGINATION"],
    }),

    // Edit fee details for origination
    editFeeDetails: builder.mutation<
      void,
      {
        map_id: string;
        data: {
          enable_frequency_direct?: boolean;
          customer_membership_frequency_direct?: string;
          currency_direct?: string;
          customer_membership_fee_direct?: number;
          customer_membership_frequency_indirect?: string;
          customer_membership_fee_indirect?: number;
          insurance_premium_logic?: string;
          enable_financing_frequency?: boolean;
          financing_fees_frequency?: string;
          enable_financing_cost?: boolean;
          insurance_cost?: number;
          overdue_additional_discount_rate?: number;
          discount_rate?: number | null;
          buffer_due_days?: number | null;
          floating_rate_margin?: number;
          sdr_on_funded_amount?: boolean;
          updated_financing_from_date?: string;
          update_average_funding_period?: string;
          updated_approved_amount?: number | null;
          advanced_amount?: number | null;
          turnover_type?: "selective" | "full" | null;
          agent_id?: string | null;
          // sdr_charged_basis?: string;
          enable_supplier_discount_rate?: boolean;
          // supplier_discount_rate?: string;
          // sales_owner?: string;
          // sales_manager?: string;
          // head_of_sales?: string;
        };
      }
    >({
      query({ map_id, data }) {
        return {
          url: `/origination/post-cc/maps/${map_id}`,
          method: "patch",
          data,
        };
      },
      invalidatesTags: () => ["ORIGINATION"],
    }),
    // Get operation map details
    GetOperationMap: builder.query<any, { map_id: string }>({
      query: ({ map_id }) => ({
        url: `/operations/post-cc/maps/${map_id}`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
      providesTags: ["OPERATION"],
    }),
    // Edit operation map info
    editMapInfo: builder.mutation<
      void,
      {
        map_id: string;
        data: {
          ipl_product?: string;
          ipl_subproduct?: string;
          map_status?: string;
          trustee?: string;
          virtual_bank_account?: string;
          virtual_bank_account_type?: string;
          supplier_virtual_account?: boolean;
          buyer_virtual_account?: boolean;
          invoice_comments?: string | null;
          haircut_rate?: number;
          rebate_offered?: number;
        };
      }
    >({
      query({ map_id, data }) {
        return {
          url: `/operations/post-cc/maps/${map_id}`,
          method: "patch",
          data,
        };
      },
      invalidatesTags: () => ["OPERATION"],
    }),
    // Get Supplier Info. details
    GetSupplierInfo: builder.query<
      any,
      { account_type: string; map_id: string }
    >({
      query: ({ account_type, map_id }) => ({
        url: `/operations/post-cc/${account_type}/maps/${map_id}`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
      providesTags: ["OPERATION"],
    }),
    GetAddInfoDetails: builder.mutation<
      void,
      {
        map_id: string;
        account_type: string;
        data: Record<string, any>;
      }
    >({
      query: ({ account_type, map_id, data }) => ({
        url: `/operations/post-cc/${account_type}/maps/${map_id}`,
        method: "patch",
        data, // <-- use 'data' instead of 'body' for axiosBaseQuery
        // Remove 'body' property, use 'data' for PATCH
        // headers: { "Content-Type": "application/json" }, // optional, axios defaults to JSON
      }),
      invalidatesTags: ["OPERATION"],
    }),
    GetBuyerInfo: builder.query<any, { account_type: string; map_id: string }>({
      query: ({ account_type, map_id }) => ({
        url: `/operations/post-cc/${account_type}/maps/${map_id}`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
      providesTags: ["OPERATION"],
    }),
    updateMapInfo: builder.mutation<
      any,
      { map_name: string; data: { [field_name: string]: any } }
    >({
      query: ({ map_name, data }) => {
        return {
          url: `/maps/${map_name}`,
          method: "patch",
          data,
        };
      },
      transformResponse: (response) => response?.data,
      invalidatesTags: ["LEGALE"],
    }),
    SupplierInfoUpdate: builder.mutation<
      any,
      { id: string; data: { [field_name: string]: any } }
    >({
      query: ({ id, data }) => {
        return {
          url: `${ACCOUNTS_V2}/${id}`,
          method: "PUT",
          data,
        };
      },
      transformResponse: (response) => response?.data,
      invalidatesTags: ["OPERATION"],
    }),
    BuyerInfoUpdate: builder.mutation<
      any,
      { id: string; data: { [field_name: string]: any } }
    >({
      query: ({ id, data }) => {
        return {
          url: `${ACCOUNTS_V2}/${id}`,
          method: "PUT",
          data,
        };
      },
      transformResponse: (response) => response?.data,
      invalidatesTags: ["OPERATION"],
    }),
    ContactDetailsUpdate: builder.mutation<
      any,
      {
        account_id: string;
        contact_id: string;
        data: any;
      }
    >({
      query: ({ account_id, contact_id, data }) => {
        return {
          url: `/accounts/${account_id}/contacts/${contact_id}`,
          method: "PUT",
          data,
        };
      },
      invalidatesTags: ["OPERATION"],
    }),

    Getallinsurer: builder.query<any, null>({
      query: () => ({ url: "/insurers", method: "get" }),
      transformResponse: (response) => response?.data,
      providesTags: ["RISK"],
    }),
    // Get insurer details including policies by insurer id
    GetInsurerById: builder.query<any, string>({
      query: (insurer_id) => ({
        url: `/insurer-policy/insurer/${insurer_id}`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
      providesTags: ["RISK"],
    }),
    AddInsurance: builder.mutation<
      any,
      { data: any; params?: { map_id?: string; insurance_type?: string } }
    >({
      query: ({ data, params }) => {
        const mapId = params?.map_id ?? data?.map_id;

        return {
          url: `/post-cc/map/${mapId}/insurances`,
          method: "post",
          data: { ...data, insurance_type: "primary-insurance" },
        };
      },
      transformResponse: (response) => response?.data,
      invalidatesTags: ["RISK"],
    }),
    AddInsuranceTopup: builder.mutation<
      any,
      { data: any; params?: { map_id?: string } }
    >({
      query: ({ data, params }) => {
        const mapId = params?.map_id ?? data?.map_id;
        return {
          url: `/post-cc/map/${mapId}/insurances`,
          method: "post",
          data: { ...data, insurance_type: "topup-insurance" },
        };
      },
      transformResponse: (response) => response?.data,
      invalidatesTags: ["RISK"],
    }),
    AddInsuranceSecondary: builder.mutation<
      any,
      { data: any; params?: { map_id?: string } }
    >({
      query: ({ data, params }) => {
        const mapId = params?.map_id ?? data?.map_id;
        return {
          url: `/post-cc/map/${mapId}/insurances`,
          method: "post",
          data: { ...data, insurance_type: "secondary-insurance" },
        };
      },
      transformResponse: (response) => response?.data,
      invalidatesTags: ["RISK"],
    }),

    UpdateInsurance: builder.mutation<
      any,
      { data: any; params?: { map_id?: string; insurance_id?: string } }
    >({
      query: ({ data, params }) => {
        const mapId = params?.map_id ?? data?.map_id;
        const insuranceId = params?.insurance_id ?? data?.insurance_id;
        return {
          url: `/patch/map/${mapId}/insurances/${insuranceId}`,
          method: "patch",
          data,
        };
      },
      transformResponse: (response) => response?.data,
      invalidatesTags: ["RISK"],
    }),
    DeleteInsurance: builder.mutation<
      any,
      { params: { map_id: string; insurance_id: string } }
    >({
      query: ({ params }) => {
        const { map_id, insurance_id } = params;
        return {
          url: `/post-cc/map/${map_id}/insurances/${insurance_id}`,
          method: "delete",
        };
      },
      transformResponse: (response) => response?.data,
      invalidatesTags: ["RISK"],
    }),
    GetTerminationDetails: builder.query<any, { map_id: string }>({
      query: ({ map_id }) => ({
        url: `/risk/post-cc/map/${map_id}/detail-summary`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
      providesTags: ["RISK"],
    }),
    GetCCDecisionDetails: builder.query<any, { map_id: string }>({
      query: ({ map_id }) => ({
        url: `/risk/post-cc/map/${map_id}/detail-summary`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
      providesTags: ["RISK"],
    }),

    UpdateCCDecisionDetails: builder.mutation<
      any,
      { map_id: string; data: { [field_name: string]: any } }
    >({
      query: ({ map_id, data }) => {
        return {
          url: `/risk/post-cc/map/${map_id}/detail-summary`,
          method: "PUT",
          data,
        };
      },
      transformResponse: (response) => response?.data,
      invalidatesTags: ["OPERATION"],
    }),
    UpdateRiskOverride: builder.mutation<
      any,
      { map_id: string; data: { [field_name: string]: any } }
    >({
      query: ({ map_id, data }) => {
        return {
          url: `/risk/post-cc/map/${map_id}/detail-summary`,
          method: "PATCH",
          data,
        };
      },
      transformResponse: (response) => response?.data,
      invalidatesTags: ["RISK"],
    }),

    GetSecurityDoc: builder.query<any, { map_id: string }>({
      query: ({ map_id }) => ({
        url: `/risk/map/${map_id}/security-documents/list`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
      providesTags: ["RISK"],
    }),
    GetOperationSecurityDoc: builder.query<any, { map_id: string }>({
      query: ({ map_id }) => ({
        url: `/operations/map/${map_id}/security-documents/document`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
      providesTags: ["OPERATION"],
    }),

    AddSecurityDocRisk: builder.mutation<
      any,
      { data: any; params?: { map_id?: string } }
    >({
      query: ({ data, params }) => {
        const mapId = params?.map_id ?? data?.map_id;

        return {
          url: `/risk/post-cc/map/${mapId}/security-documents`,
          method: "post",
          data,
        };
      },
      transformResponse: (response) => response?.data,
      invalidatesTags: ["RISK"],
    }),

    UpdateSecurityDocRisk: builder.mutation<
      any,
      { data: any; params?: { map_id?: string; securityId?: string } }
    >({
      query: ({ data, params }) => {
        const mapId = params?.map_id ?? data?.map_id;
        const securityId = params?.securityId ?? data?.securityId;

        return {
          url: `/risk/post-cc/map/${mapId}/security-documents/${securityId}`,
          method: "patch",
          data,
        };
      },
      transformResponse: (response) => response?.data,
      invalidatesTags: ["RISK"],
    }),
    DeleteSecurityDocRisk: builder.mutation<
      any,
      { data: any; params?: { map_id?: string; securityId?: string } }
    >({
      query: ({ data, params }) => {
        const mapId = params?.map_id ?? data?.map_id;
        const securityId = params?.securityId ?? data?.securityId;

        return {
          url: `/risk/post-cc/map/${mapId}/security-documents/${securityId}`,
          method: "delete",
          data,
        };
      },
      transformResponse: (response) => response?.data,
      invalidatesTags: ["RISK"],
    }),

    TerminationUpdate: builder.mutation<
      any,
      { map_id: string; data: { [field_name: string]: any } }
    >({
      query: ({ map_id, data }) => {
        return {
          url: `/risk/post-cc/map/${map_id}/detail-summary`,
          method: "PUT",
          data,
        };
      },

      transformResponse: (response) => response?.data,
      invalidatesTags: ["RISK"],
    }),
    GetAllInsurerDetails: builder.query<any, { map_id: string }>({
      query: ({ map_id }) => ({
        url: `/post-cc/map/${map_id}/insurances`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
      providesTags: ["RISK"],
    }),
    getDocumentTemplate: builder.query<any, { map_id: string }>({
      query: ({ map_id }) => ({
        url: `/risk/map/${map_id}/security-required-documents/template`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
      providesTags: ["RISK"],
    }),
    GetAllMapAttachments: builder.query<any, { map_id: string }>({
      query: ({ map_id }) => ({
        url: `/operations/map/${map_id}/map-legal-documents/document`,
        method: "get",
      }),
      transformResponse: (response) => {
        return response?.data; // return only data part
      },
      providesTags: ["LEGALE"],
    }),

    GetAllsecurityDocOperationAttachments: builder.query<
      any,
      { map_id: string }
    >({
      query: ({ map_id }) => ({
        url: `/operations/map/${map_id}/map-legal-documents/document`,
        method: "get",
      }),
      transformResponse: (response) => {
        return response?.data; // return only data part
      },
      providesTags: ["LEGALE"],
    }),

    // Map Attachments (ARPA, PSA, IOF)
    GetMapAttachments: builder.query<any, { map_id: string }>({
      query: ({ map_id }) => ({
        url: `/operations/map/${map_id}/map-legal-documents/template`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
      providesTags: ["OPERATION"],
    }),
    UploadMapAttachment: builder.mutation<
      any,
      {
        map_id: string;
        attachment_type: string;
        file: File;
        note?: string;
        document_type?: string; // optional explicit document type id
        document_category?: string; // optional category override
      }
    >({
      query: ({
        map_id,
        attachment_type,
        file,
        note,
        document_type,
        document_category,
      }) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("attachment_type", attachment_type);
        formData.append("document_type", document_type || attachment_type);
        formData.append("document_category", document_category || "legal");
        if (note) formData.append("note", note);
        return {
          url: `/operations/map/${map_id}/map-legal-documents/document`,
          method: "post",
          data: formData,
          headers: { "Content-Type": "multipart/form-data" },
        };
      },
      transformResponse: (response) => response?.data,
      invalidatesTags: ["OPERATION"],
    }),

    UploadsecurityDocOperationAttachment: builder.mutation<
      any,
      {
        map_id: string;
        attachment_type: string;
        document_id: string;
        file: File;
        note?: string;
        document_type?: string; // optional explicit document type id
        document_category?: string; // optional category override
      }
    >({
      query: ({
        map_id,
        attachment_type,
        document_id,
        file,
        note,
        document_type,
        document_category,
      }) => {
        const formData = new FormData();
        formData.append("file", file);
        // formData.append("attachment_type", document_id);
        formData.append("document_type", document_type || attachment_type);
        formData.append("document_category", document_category || "operation");
        if (note) formData.append("note", note);
        return {
          url: `/operations/map/${map_id}/security-documents/${document_id}/document`,
          method: "post",
          data: formData,
          headers: { "Content-Type": "multipart/form-data" },
        };
      },
      transformResponse: (response) => response?.data,
      invalidatesTags: ["OPERATION"],
    }),
    // Fetch a single map legal document (view)
    GetMapAttachmentDocument: builder.query<
      any,
      { map_id: string; document_id: string }
    >({
      query: ({ map_id, document_id }) => ({
        url: `/operations/map/${map_id}/documents/${document_id}`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
      providesTags: ["OPERATION"],
    }),

    DeleteMapAttachmentDocument: builder.mutation<
      any,
      { map_id: string; document_id: string }
    >({
      query: ({ map_id, document_id }) => ({
        url: `/operations/map/${map_id}/documents/${document_id}`,
        method: "delete",
      }),
      transformResponse: (response) => response?.data,
      invalidatesTags: ["OPERATION"],
    }),
    DeleteInsuranceDocument: builder.mutation<
      any,
      { map_id: string; document_id: string; insurance_id: string }
    >({
      query: ({ map_id, document_id, insurance_id }) => ({
        url: `/map/${map_id}/insurance_documents/${insurance_id}/document/${document_id}`,
        method: "delete",
      }),
      transformResponse: (response) => response?.data,
      invalidatesTags: ["RISK"],
    }),
    // Insurance documents list (template / types)
    GetInsuranceDocuments: builder.query<
      any,
      { map_id: string; insurance_type?: string }
    >({
      query: ({ map_id, insurance_type }) => ({
        url: `/map/${map_id}/insurance_documents/template`,
        method: "get",
        params: insurance_type ? { insurance_type } : undefined,
      }),
      transformResponse: (response) => response?.data,
      providesTags: ["RISK"],
    }),
    // Single insurance document (metadata / url)
    GetInsuranceDocumentUrl: builder.query<
      any,
      { map_id: string; document_id: string; insurance_id: string }
    >({
      query: ({ map_id, document_id, insurance_id }) => ({
        url: `/map/${map_id}/insurance_documents/${insurance_id}/document/${document_id}`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
      providesTags: ["RISK"],
    }),
    // Upload insurance document (if missing previously)
    AddInsuranceDocument: builder.mutation<
      any,
      {
        map_id: string;
        insurance_id: string;
        file: File;
        document_type: string;
        note?: string;
      }
    >({
      query: ({ map_id, insurance_id, file, document_type, note }) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("document_type", document_type);
        formData.append("document_category", "legal");
        if (note) formData.append("note", note);
        return {
          url: `/map/${map_id}/insurance_documents/${insurance_id}/document`,
          method: "post",
          data: formData,
          headers: { "Content-Type": "multipart/form-data" },
        };
      },
      transformResponse: (response) => response?.data,
      invalidatesTags: ["RISK"],
    }),

    // Consent Letter APIs
    GetConsentLetterDocument: builder.query<
      any,
      { map_name: string; document_id: string }
    >({
      query: ({ map_name, document_id }) => ({
        url: `/legal/map/${map_name}/consent-letter/${document_id}`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
      providesTags: ["LEGALE"],
    }),

    DeleteConsentLetterDocument: builder.mutation<
      any,
      { map_name: string; document_id: string }
    >({
      query: ({ map_name, document_id }) => ({
        url: `/legal/map/${map_name}/consent-letter/${document_id}`,
        method: "delete",
      }),
      transformResponse: (response) => response?.data,
      invalidatesTags: ["LEGALE"],
    }),

    UploadConsentLetterDocument: builder.mutation<
      any,
      {
        map_name: string;
        file: File;
        note?: string;
      }
    >({
      query: ({ map_name, file, note }) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("document_type", "consent-letter");
        if (note) formData.append("note", note);
        return {
          url: `/legal/map/${map_name}/consent-letter`,
          method: "post",
          data: formData,
          headers: { "Content-Type": "multipart/form-data" },
        };
      },
      transformResponse: (response) => response?.data,
      invalidatesTags: ["LEGALE"],
    }),
    GetAllConsentLetterDocuments: builder.query<any, { map_name: string }>({
      query: ({ map_name }) => ({
        url: `/legal/map/${map_name}/consent-letter`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
      providesTags: ["LEGALE"],
    }),

    GetConsentLetterTemplate: builder.query<
      any,
      { account_relationship_id: string }
    >({
      query: ({ account_relationship_id }) => ({
        url: "/legal/consent-letter",
        method: "get",
        params: { account_relationship_id },
      }),
      transformResponse: (response) => response?.data ?? response,
      providesTags: ["LEGALE"],
    }),

    GetConsentLetterEmailTemplate: builder.query<
      any,
      { account_relationship_id: string }
    >({
      query: ({ account_relationship_id }) => ({
        url: "/legal/consent-letter/email-template",
        method: "get",
        params: { account_relationship_id },
      }),
      transformResponse: (response) => response?.data ?? response,
      keepUnusedDataFor: 300,
    }),

    GetSignedConsentLetter: builder.query<
      any,
      { account_relationship_id: string }
    >({
      query: ({ account_relationship_id }) => ({
        url: "/singed/consent-letter",
        method: "get",
        params: { account_relationship_id },
      }),
      transformResponse: (response) => response?.data ?? response,
      keepUnusedDataFor: 300,
    }),

    DownloadSignedConsentLetter: builder.query<
      any,
      { account_relationship_id: string }
    >({
      query: ({ account_relationship_id }) => ({
        url: "/singed/consent-letter/download",
        method: "get",
        params: { account_relationship_id },
      }),
      transformResponse: (response) => response?.data ?? response,
    }),

    UploadSignedConsentLetter: builder.mutation<
      any,
      { account_relationship_id: string; file: File }
    >({
      query: ({ account_relationship_id, file }) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("account_relationship_id", account_relationship_id);
        formData.append("document_type", "singed-consent-latter");

        return {
          url: "/singed/consent-letter",
          method: "post",
          data: formData,
          headers: { "Content-Type": "multipart/form-data" },
        };
      },
      transformResponse: (response) => response?.data ?? response,
      invalidatesTags: ["LEGALE"],
    }),

    SendConsentLetter: builder.mutation<
      any,
      { account_relationship_id: string; document_id?: string }
    >({
      query: ({ account_relationship_id, document_id }) => ({
        url: "/legal/consent-letter",
        method: "post",
        data: {
          account_relationship_id,
          ...(document_id ? { document_id } : {}),
        },
      }),
      transformResponse: (response) => response?.data ?? response,
      invalidatesTags: ["LEGALE"],
    }),

    // Docusign Templates (document_templates table)
    GetDocumentTemplates: builder.query<any, void>({
      query: () => ({
        url: `/maps/document-templates`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
      providesTags: ["DOCUSIGN_TEMPLATE"],
      keepUnusedDataFor: 15 * 60,
    }),

    // Send docusign envelope using selected template + current map
    SendDocusignData: builder.mutation<
      any,
      {
        map_id: string;
        template_id: string;
        preview_only?: boolean;
        preview_document?: boolean;
        envelope_id?: string;
        signer_email?: string;
        signer_name?: string;
        second_signer_email?: string;
        second_signer_name?: string;
        second_signer_designation?: string;
        cc_emails?: string[];
        invoice_ids?: string[];
      }
    >({
      query: ({
        map_id,
        template_id,
        preview_only,
        preview_document,
        envelope_id,
        signer_email,
        signer_name,
        second_signer_email,
        second_signer_name,
        second_signer_designation,
        cc_emails,
        invoice_ids,
      }) => ({
        url: `/maps/${map_id}/docusign-data`,
        method: "post",
        data: {
          templateId: template_id,
          data: { mapId: map_id },
          ...(preview_only ? { previewOnly: true } : {}),
          ...(preview_document ? { previewDocument: true } : {}),
          ...(envelope_id ? { envelopeId: envelope_id } : {}),
          ...(signer_email ? { signerEmail: signer_email } : {}),
          ...(signer_name ? { signerName: signer_name } : {}),
          ...(second_signer_email
            ? { secondSignerEmail: second_signer_email }
            : {}),
          ...(second_signer_name
            ? { secondSignerName: second_signer_name }
            : {}),
          ...(second_signer_designation
            ? { secondSignerDesignation: second_signer_designation }
            : {}),
          ...(cc_emails?.length ? { ccEmails: cc_emails } : {}),
          ...(invoice_ids?.length ? { invoiceIds: invoice_ids } : {}),
        },
      }),
      transformResponse: (response: any) => response?.data ?? response,
      invalidatesTags: ["OPERATION", "DOCUSIGN_TEMPLATE"],
    }),

    // Latest envelope status (mirrors DocuSign webhook updates in document_webhooks)
    GetDocusignEnvelopeStatus: builder.query<
      {
        envelope_id: string;
        status: string | null;
        event_received_at?: string | null;
        updated_at?: string | null;
      },
      { map_id: string; envelope_id: string }
    >({
      query: ({ map_id, envelope_id }) => ({
        url: `/maps/${map_id}/docusign-envelope-status`,
        method: "get",
        params: { envelope_id },
      }),
      transformResponse: (response: any) => response?.data ?? response,
      // Don't bind this query to the broad OPERATION invalidation.
      // This prevents repeated refetching while operators are working.
      providesTags: [],
    }),
    GetDocusignTemplateStatuses: builder.query<
      {
        count: number;
        arpa_available?: boolean;
        psa_available?: boolean;
        items: Array<{
          envelope_id: string;
          map_name?: string | null;
          template_id?: string | null;
          template_name?: string | null;
          sent_by_email?: string | null;
          signer_name?: string | null;
          signer_email?: string | null;
          manager_email?: string | null;
          cc_emails?: Array<string | { email?: string; name?: string }>;
          invoice_ids?: Array<string>;
          status?: string | null;
          sent_at?: string | null;
          event_received_at?: string | null;
          updated_at?: string | null;
          created_at?: string | null;
          signed_document_available?: boolean;
          signed_party?: "buyer" | "seller" | null;
        }>;
      },
      { map_id: string }
    >({
      query: ({ map_id }) => ({
        url: `/maps/${map_id}/docusign-template-statuses`,
        method: "get",
      }),
      transformResponse: (response: any) => response?.data ?? response,
      providesTags: ["DOCUSIGN_TEMPLATE"],
    }),
    GetDocusignSignedDocumentUrl: builder.mutation<
      {
        viewUrl: string;
        source: "s3";
        viewType: "pdf";
        expiresInSeconds: number;
      },
      { map_id: string; envelope_id: string }
    >({
      query: ({ map_id, envelope_id }) => ({
        url: `/maps/${map_id}/docusign/${envelope_id}/view-url`,
        method: "post",
      }),
      transformResponse: (response: any) => response?.data ?? response,
    }),
  }),
});

export const {
  useGetMapRebateQuery,
  useUpdateMapRebateMutation,
  useGetAllAgentsQuery,
  useGetFeeDetailsQuery,
  useEditFeeDetailsMutation,
  useGetOperationMapQuery,
  useEditMapInfoMutation,
  useGetSupplierInfoQuery,
  useGetBuyerInfoQuery,
  useUpdateMapInfoMutation,
  useGetAddInfoDetailsMutation,
  useSupplierInfoUpdateMutation,
  useBuyerInfoUpdateMutation,
  useContactDetailsUpdateMutation,
  useGetallinsurerQuery,
  useGetInsurerByIdQuery,

  useAddInsuranceMutation,
  useAddInsuranceTopupMutation,
  useUpdateInsuranceMutation,
  useDeleteInsuranceMutation,
  useGetAllInsurerDetailsQuery,
  useGetTerminationDetailsQuery,
  useTerminationUpdateMutation,
  useAddInsuranceSecondaryMutation,
  useGetDocumentTemplateQuery,
  useGetInsuranceDocumentsQuery,
  useGetInsuranceDocumentUrlQuery,
  useAddInsuranceDocumentMutation,
  useDeleteInsuranceDocumentMutation,
  useGetCCDecisionDetailsQuery,
  useUpdateCCDecisionDetailsMutation,
  useUpdateRiskOverrideMutation,
  useGetSecurityDocQuery,
  useAddSecurityDocRiskMutation,
  useUpdateSecurityDocRiskMutation,
  useDeleteSecurityDocRiskMutation,
  useGetMapAttachmentsQuery,
  useUploadMapAttachmentMutation,
  useGetMapAttachmentDocumentQuery,
  useLazyGetMapAttachmentDocumentQuery,
  useGetAllMapAttachmentsQuery,
  useDeleteMapAttachmentDocumentMutation,
  useGetOperationSecurityDocQuery,
  useUploadsecurityDocOperationAttachmentMutation,
  useGetAllsecurityDocOperationAttachmentsQuery,
  useGetConsentLetterDocumentQuery,
  useLazyGetConsentLetterDocumentQuery,
  useDeleteConsentLetterDocumentMutation,
  useUploadConsentLetterDocumentMutation,
  useGetAllConsentLetterDocumentsQuery,
  useGetConsentLetterTemplateQuery,
  useLazyGetConsentLetterEmailTemplateQuery,
  useGetSignedConsentLetterQuery,
  useLazyGetSignedConsentLetterQuery,
  useLazyDownloadSignedConsentLetterQuery,
  useUploadSignedConsentLetterMutation,
  useSendConsentLetterMutation,
  useGetDocumentTemplatesQuery,
  useSendDocusignDataMutation,
  useGetDocusignEnvelopeStatusQuery,
  useGetDocusignTemplateStatusesQuery,
  useGetDocusignSignedDocumentUrlMutation,
} = postcc;
